import React from 'react';
import { AI_TOOLS } from '@constants';
import { TrackingEvent } from '@classes/TrackingEvent';
import { submitQueries, submitQueriesStream, submitCitationStream } from '@services/genaiQueriesV2';
import { getKeyValue } from '@services/agentMemory';
import { addAdvancedSettingValues, useAdvancedSettings } from '../application/useAdvancedSettings';
import { logFormSubmit } from '@utils/usefulFuncs';
import { addOptionalFreeField } from '@utils/advancedSettings';

async function resolveMemoryExtraContext(tool, extraContext, memoryKey) {
  if (tool !== AI_TOOLS.QUERY || extraContext || !memoryKey) {
    return extraContext;
  }

  const memoryValue = await getKeyValue(memoryKey);
  if (memoryValue === null || memoryValue === undefined) {
    return null;
  }

  return typeof memoryValue === 'string' ? memoryValue : JSON.stringify(memoryValue);
}

export async function getResultId(question, params, options) {
  const {
    selectedDocs,
    screen_mode,
    tickers,

    tool,

    extraContext,
    recordId,

    model,

    enableFollowUpQuestions,
    enableContext,

    doc_types,
    start_date,
    end_date,
  } = params;
  const { isResearchMode, isAPICitationsMode, nonStream } = options;

  TrackingEvent.create('Asked Digital Analyst Question', {
    Question: question,
    Model: model,
    Tickers: tickers,
    'Selected Docs': selectedDocs?.map((d) => d.filename),
    'Inserting Citations': isAPICitationsMode,
  });

  if (isAPICitationsMode) {
    logFormSubmit({ user_mode: tool, query_run_id: recordId });
    const result = await submitCitationStream({ queryRunId: recordId });
    if (result.status === 200 && !result.error && result.runId) {
      return { id: result.runId };
    }
    TrackingEvent.create('Asked Digital Analyst Question Failed', {
      Question: question,
      'Inserting Citations': true,
      Error: result.error || 'unknown',
    });
    return { error: result.error || 'Something went wrong, try again.' };
  }

  const docIds = selectedDocs?.map((doc) => doc.id || doc.doc_id);
  const settings = {
    user_mode: tool,
    screen_mode,
    research_mode: isResearchMode,
    extra_context: extraContext,
  };
  addOptionalFreeField('doc_types', doc_types, settings);
  addOptionalFreeField('start_date', start_date, settings);
  addOptionalFreeField('end_date', end_date, settings);
  addAdvancedSettingValues(params, settings, ['extra_context', 'start_date', 'end_date', 'doc_types', 'ticker']);
  logFormSubmit({ ...settings, question, doc_ids: docIds, ticker: tickers });

  const submitFn = nonStream ? submitQueries : submitQueriesStream;
  const result = await submitFn({
    questions: question,
    ticker: tickers,
    docIds,
    settings,
  });
  if (result.status === 200) {
    if (nonStream) {
      return result.data;
    }
    return { id: result.runId };
  }
  TrackingEvent.create('Asked Digital Analyst Question Failed', {
    Question: question,
    'Inserting Citations': false,
    Error: result.error || 'unknown',
  });
  return { error: result.error || 'Something went wrong, try again.' };
}

export const useAskDigitalAnalystSubmit = (tool) => {
  const advancedSettingsValues = useAdvancedSettings();
  const queryMemoryKey = advancedSettingsValues.memory_key;

  const onSubmit = React.useCallback(
    async ({ prompt, docIds, selectedDocs, extraContext }) => {
      TrackingEvent.create('Asked Digital Analyst Question', {
        Question: prompt,
        Model: advancedSettingsValues.model,
        'Selected Docs': selectedDocs?.map((d) => d.filename),
      });

      const resolvedDocIds = docIds || selectedDocs?.map((doc) => doc.id);
      const resolvedExtraContext = await resolveMemoryExtraContext(tool, extraContext, queryMemoryKey);
      if (tool === AI_TOOLS.QUERY && queryMemoryKey && !extraContext && !resolvedExtraContext) {
        return {
          error: 'Unable to load the selected memory.',
        };
      }
      const settings = { user_mode: tool };
      if (resolvedExtraContext) {
        settings.extra_context = resolvedExtraContext;
      }
      addAdvancedSettingValues(advancedSettingsValues, settings, ['extra_context', 'memory_key']);
      const form = { question: prompt, doc_ids: resolvedDocIds, ...settings };
      logFormSubmit(form);
      const result = await submitQueriesStream({ questions: prompt, docIds: resolvedDocIds, settings });
      if (result.status === 200) {
        return {
          resultId: result.runId,
          form,
        };
      } else {
        TrackingEvent.create('Asked Digital Analyst Question Failed', {
          Question: prompt,
          Error: result.error || 'unknown',
        });
        return {
          error: result.error || 'Something went wrong, try again.',
        };
      }
    },
    [tool, advancedSettingsValues, queryMemoryKey]
  );

  return onSubmit;
};

export const useAskDigitalAnalyst = (tool) => {
  const advancedSettingsValues = useAdvancedSettings();
  const { model, generate_follow_ups, memory_key } = advancedSettingsValues;

  const [prevUsedModel, setPrevUsedModel] = React.useState(model);
  const [resultId, setResultId] = React.useState(null);
  const [citationsResultId, setCitationsResultId] = React.useState(null);
  const [questionAsked, setQuestionAsked] = React.useState(null);
  const [sourceDocs, setSourceDocs] = React.useState([]);

  const [formError, setFormError] = React.useState(null);
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);

  const onSubmit = async ({
    question,
    forceQuestionAsked,
    selectedDocs,
    forceFollowUpQuestionOff,
    ticker,
    model: forceModel,
    extra_context,
    isCitationsMode,
    record_id,
    isAPICitationsMode,
    isRFPMode,
    isEvestmentMode,
  }) => {
    const hasMemoryKeySelected = tool === AI_TOOLS.QUERY && !!memory_key;

    if (selectedDocs?.length === 0 && !hasMemoryKeySelected && !isAPICitationsMode && !isRFPMode && !isEvestmentMode) {
      setFormError('Please select at least one ticker or document to run a query');
      return;
    }
    if (question === '' && !isAPICitationsMode) {
      setFormError('Please enter a question to run a query on the selected tickers/documents.');
      return;
    }

    if (!isCitationsMode) {
      setSourceDocs(selectedDocs);
    }
    const _question = question;
    const _model = forceModel || model;
    setPrevUsedModel(_model);
    if (!isCitationsMode) {
      setCitationsResultId(null);
    }
    const resolvedExtraContext = !isAPICitationsMode
      ? await resolveMemoryExtraContext(tool, extra_context, memory_key)
      : extra_context;
    if (tool === AI_TOOLS.QUERY && memory_key && !extra_context && !resolvedExtraContext) {
      setFormError('Unable to load the selected memory.');
      return;
    }

    TrackingEvent.create('Asked Digital Analyst Question', {
      Question: _question,
      Model: _model,
      Tickers: ticker,
      'Selected Docs': selectedDocs?.map((d) => d.filename),
      'Inserting Citations': isAPICitationsMode,
    });

    setFormError(false);
    if (!isCitationsMode) {
      setResultId(null);
    }
    setIsSubmittingToApi(true);
    if (!isAPICitationsMode) {
      setQuestionAsked(forceQuestionAsked || question);
    }

    if (isAPICitationsMode) {
      logFormSubmit({ user_mode: tool, query_run_id: record_id, ticker });
      const result = await submitCitationStream({ queryRunId: record_id });
      setIsSubmittingToApi(false);
      if (result.status === 200 && !result.error && result.runId) {
        isCitationsMode ? setCitationsResultId(result.runId) : setResultId(result.runId);
        setFormError(false);
      } else {
        if (!isCitationsMode) setResultId(null);
        setFormError(result.error || 'Something went wrong, try again.');
        TrackingEvent.create('Asked Digital Analyst Question Failed', {
          Question: _question,
          'Inserting Citations': true,
          Error: result.error || 'unknown',
        });
      }
      return;
    }

    const docIds = selectedDocs?.map((doc) => doc.id);
    const settings = {
      model: _model,
      user_mode: tool,
    };
    if (resolvedExtraContext) settings.extra_context = resolvedExtraContext;
    addAdvancedSettingValues(advancedSettingsValues, settings, ['model', 'memory_key']);
    logFormSubmit({ ...settings, question: _question, doc_ids: docIds, ticker });

    const result = await submitQueriesStream({
      questions: _question,
      ticker,
      docIds,
      settings,
    });
    setIsSubmittingToApi(false);
    if (result.status === 200) {
      isCitationsMode ? setCitationsResultId(result.runId) : setResultId(result.runId);
      setFormError(false);
    } else {
      if (!isCitationsMode) setResultId(null);
      setFormError(result.error || 'Something went wrong, try again.');
      TrackingEvent.create('Asked Digital Analyst Question Failed', {
        Question: _question,
        'Inserting Citations': false,
        Error: result.error || 'unknown',
      });
    }
  };

  const onClearResult = () => {
    setResultId(null);
    setCitationsResultId(null);
    setFormError(null);
    setSourceDocs([]);
  };

  return {
    onSubmit,
    onClearResult,
    sourceDocs,

    resultId,
    citationsResultId,

    prevUsedModel,
    questionAsked,
    formError,
    isSubmittingToApi,
  };
};
